import { useState, useEffect } from "react";
import CollapsibleExample from "../../Components/Navbar";
import "../../Assets/Styles/inquiry.scss";
import axios from "axios";
import { Form, Button, Container, Table } from "react-bootstrap";
import { FaCheck, FaTrash } from "react-icons/fa6";
import NepaliDatePicker, { NepaliDate } from "@zener/nepali-datepicker-react";
import SelectSearchInput from "../../Components/SelectSearch";
import SelectCustomer from "../../Components/SelectSearch/SearchInquiryCustomer";

const InquiryPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const url = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem('tokens');
  const [hallList, setHallList] = useState([]);
  const [selectedHall, setSelectedHall] = useState("");
  const [nepaliDate, setNepaliDate] = useState("");
  const [englishDate, setEnglishDate] = useState("");

  const parseNepaliDate = (value) => {
    try {
      let date;
      let nepaliDateStr = "";

      if (value && typeof value === 'object' && value.bsDate) {
        // Convert Nepali numerals to English digits
        const englishDate = value.bsDate.replace(/[०-९]/g, d => '०१२३४५६७८९'.indexOf(d));
        const [year, month, day] = englishDate.split('-').map(Number);
        date = new NepaliDate(year, month - 1, day);
        nepaliDateStr = date.format('YYYY-MM-DD');
      } else if (value instanceof NepaliDate) {
        nepaliDateStr = value.format('YYYY-MM-DD');
      } else if (typeof value === 'string') {
        nepaliDateStr = value;
      }

      return nepaliDateStr;
    } catch (error) {
      console.error("Error parsing Nepali date:", error);
      return null;
    }
  };

  const formattedNepaliDate = parseNepaliDate(nepaliDate);

  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: "",
    contact_no: "",
    address: "",
    country: "Nepal",
    reservation_date: "",
    about: "",
    email: "",
    reservation_miti: "",
    hallname: "",
    timeslot: ""
  });

  useEffect(() => {
    const fetchHalls = async () => {
      try {
        setLoading(true);
        const response = await axios.post(`${url}/halls`, { token });
        setHallList(response.data);
      } catch (error) {
        console.error("Failed to fetch halls", error);
        setError("Failed to load hall data");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchHalls();
  }, [token, url]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/getInquiries`, {
        params: { token }
      });
      setInquiries(response.data.data);
    } catch (error) {
      console.error("Failed to fetch inquiries", error);
      setError("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchInquiries();
  }, [token, url]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const submissionData = {
        ...formData,
        hallname: selectedHall,
        reservation_miti: formattedNepaliDate,
        reservation_date: englishDate,
        token: token
      };

      if (!nepaliDate) alert("Reservation miti is required");

      if (!selectedHall) alert("Please select a hall");

      await axios.post(`${url}/submitInquiry`, submissionData);
      alert("Inquiry submitted successfully!");
      setFormData({
        customer_name: "",
        contact_no: "",
        email: "",
        address: "",
        country: "Nepal",
        reservation_date: "",
        reservation_miti: "",
        about: "",
        hallname: "",
        timeslot: "",
      });
      setSelectedHall("");
      fetchInquiries();
      setNepaliDate("");
      setEnglishDate("");
      setSelectedHall("");
    } catch (error) {
      console.error("Submission failed", error);
      alert("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInquiry = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inquiry?")) return;

    try {
      setLoading(true);
      await axios.delete(`${url}/deleteInquiry`, {
        data: { token, id }
      });
      alert("Inquiry deleted successfully!");
      fetchInquiries();
    } catch (error) {
      console.error("Failed to delete inquiry", error);
      alert("Failed to delete inquiry");
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToBooking = (inquiry) => {
    const { token, about, ...bookingData } = inquiry;

    const params = new URLSearchParams();

    Object.entries(bookingData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });

    window.open(`/register?${params.toString()}`, '_blank');
  };

  const handleCustomerSelect = (customer) => {
    if (!customer) {
      setIsFiltered(false);
      return;
    }

    const filtered = inquiries.filter(inquiry =>
      inquiry.id === customer.id ||
      inquiry.contact_no === customer.contact_no
    );

    setFilteredInquiries(filtered);
    setIsFiltered(true);
  };

  return (
    <div>
      <CollapsibleExample />
      <Container className="inquiry-form">
        <h5>Reservation Inquiry</h5>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form onSubmit={handleSubmit} className="form-div">
          <Form.Group className="mb-3" controlId="customer_name">
            <Form.Label>Customer Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter customer name"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              required
              minLength={2}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="contact_no">
            <Form.Label>Contact No</Form.Label>
            <Form.Control
              type="tel"
              placeholder="Enter contact number"
              name="contact_no"
              value={formData.contact_no}
              onChange={handleChange}
              required
              pattern="[0-9]{10}"
              title="Please enter a 10-digit phone number"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </Form.Group>

          <div className="row-controls">
            <Form.Group controlId="address">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group controlId="country">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
            </Form.Group>
          </div>

          <div className="row-controls">
            <Form.Group controlId="reservation_date">
              <Form.Label>Reservation Date (BS)</Form.Label>
              <NepaliDatePicker
                value={nepaliDate}
                placeholder="Select BS date"
                converterMode
                lang="en"
                onChange={(e) => {
                  setNepaliDate(e);
                  try {
                    const engDate = e ? new NepaliDate(e).toADasDate() : null;
                    setEnglishDate(engDate);
                  } catch (err) {
                    console.error("Error converting BS to AD:", err);
                    setEnglishDate(null);
                  }
                }}
              />
            </Form.Group>

            <Form.Group controlId="reservation_miti">
              <Form.Label>Reservation Date (AD)</Form.Label>
              <NepaliDatePicker
                value={nepaliDate ? new NepaliDate(nepaliDate).toADasDate() : null}
                placeholder="Select AD date"
                type="AD"
                lang="en"
                converterMode
                onChange={(e) => {
                  setNepaliDate(e ? new NepaliDate(e) : null);
                  setEnglishDate(e);
                }}
              />
            </Form.Group>
          </div>

          <div className="row-controls">
            <Form.Group controlId="hallname">
              <Form.Label>Hall Selection</Form.Label>
              <SelectSearchInput
                defaultName={selectedHall}
                List={hallList}
                text={"Select Hall"}
                setSelectedItem={setSelectedHall}
              />
            </Form.Group>

            <Form.Group controlId="timeslot">
              <Form.Label>Time Slot</Form.Label>
              <div className="radio-type">
                <Form.Check
                  type="radio"
                  id="lunch"
                  label="Lunch"
                  name="timeslot"
                  value="Lunch"
                  checked={formData.timeslot === "Lunch"}
                  onChange={handleChange}
                  required
                />
                <Form.Check
                  type="radio"
                  id="dinner"
                  label="Dinner"
                  name="timeslot"
                  value="Dinner"
                  checked={formData.timeslot === "Dinner"}
                  onChange={handleChange}
                />
                <Form.Check
                  type="radio"
                  id="both"
                  label="Both"
                  name="timeslot"
                  value="Both"
                  checked={formData.timeslot === "Both"}
                  onChange={handleChange}
                />
              </div>
            </Form.Group>
          </div>

          <Form.Group className="mb-3 pb-3" controlId="about">
            <Form.Label>Inquiry Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Write something..."
              name="about"
              value={formData.about}
              onChange={handleChange}
              required
              minLength={10}
            />
          </Form.Group>

          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? "Submitting..." : "Submit Inquiry"}
          </Button>
        </Form>
      </Container>

      <Container className="inquiry-table-container">
        <h5 className="table-title">Inquiry List</h5>

        <SelectCustomer
          data={inquiries}
          onSelect={handleCustomerSelect}
        />

        {loading ? (
          <div className="loading-spinner">Loading inquiries...</div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="inquiry-table">
              <thead className="table-header">
                <tr>
                  <th>SN</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Country</th>
                  <th>Hall</th>
                  <th>Reservation Date</th>
                  <th>Reservation Miti</th>
                  <th>Time Slot</th>
                  <th>Details</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(isFiltered ? filteredInquiries : inquiries).map((inquiry, index) => (
                  <tr key={inquiry.id || index} className="table-row">
                    <td>{index + 1}</td>
                    <td>{inquiry.customer_name || 'N/A'}</td>
                    <td>{inquiry.contact_no || 'N/A'}</td>
                    <td>{inquiry.email || 'N/A'}</td>
                    <td>{inquiry.address || 'N/A'}</td>
                    <td>{inquiry.country || 'N/A'}</td>
                    <td>{inquiry.hallname || 'N/A'}</td>
                    <td>
                      {inquiry.reservation_date
                        ? new Date(inquiry.reservation_date).toISOString().split('T')[0]
                        : 'N/A'}
                    </td>
                    <td>
                      {inquiry.reservation_miti || 'N/A'}
                    </td>
                    <td>{inquiry.timeslot || 'N/A'}</td>
                    <td className="details-cell">
                      <div
                        className="details-content"
                        data-fulltext={inquiry.about || 'N/A'}
                        title={inquiry.about || 'N/A'} 
                      >
                        {inquiry.about || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Button
                          variant="success"
                          size="sm"
                          className="action-btn"
                          onClick={() => handleConvertToBooking(inquiry)}
                          disabled={loading}
                          title="Convert to Booking"
                        >
                          <FaCheck />
                        </Button>

                        <Button
                          variant="danger"
                          size="sm"
                          className="action-btn"
                          onClick={() => handleDeleteInquiry(inquiry.id)}
                          disabled={loading}
                          title="Delete Inquiry"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Container>
    </div>
  );
};

export default InquiryPage;